<template>
    <div class="licence-product flex-row">
        <span class="licence-product-title bold">{{ product.title }}</span>
        <div class="flex-column licence-product-inner">
            <!-- Pricings List -->
            <div class="flex-column-reverse flex-column ov-reset">
                <div
                    v-for="(pricing, index) in paidLicences"
                    :key="index"
                    class="licence-product-row flex-row ai-center px-3 prelative"
                    :class="{ 'mb-3': index !== 0 }"
                >
                    <!-- Quantity -->
                    <QuantityProduct
                        :price="pricing"
                        :isSaleUser="isSaleUser"
                        :product="modelValue"
                        :freeLicences="freeLicences"
                        :saleLimitation="saleLimitation"
                        @update="(val) => update(val, index)"
                    />

                    <!-- Select pricing -->
                    <template v-if="isSaleUser">
                        <el-tooltip
                            :disabled="currentProductPlan.amountInCents !== pricing.unit_amount"
                            placement="bottom-start"
                        >
                            <template #content>
                                <span v-html="amountLabel"></span>
                            </template>
                            <el-select
                                v-model="pricing.unit_amount"
                                class="licence-product-amount ml-3"
                                :disabled="
                                    currentProductPlan.amountInCents === pricing.unit_amount &&
                                    pricing.up_to === 'inf'
                                "
                                @change="emit"
                            >
                                <el-option
                                    v-for="amount in allowedAmounts"
                                    :key="amount.amount"
                                    :disabled="
                                        disableAmount(amount.amount) &&
                                        amount.amount !== pricing.unit_amount
                                    "
                                    :label="amount.label"
                                    :value="amount.amount"
                                >
                                </el-option>
                            </el-select>
                        </el-tooltip>
                    </template>
                    <span v-else class="ml-4 fs-3 semibold">
                        {{
                            $filters.numberFormatToFloatWithOptions(
                                getPriceAmountByMonth(pricing.unit_amount),
                                numformat,
                                false,
                            )
                        }}
                    </span>
                    <span :class="{ 'ml-2': isSaleUser, 'semibold ml-1': !isSaleUser }"
                        >€<span class="fs-2 regular">{{ $l10n.__('/mois') }}</span></span
                    >

                    <!-- Tag Free licences -->
                    <el-tag
                        v-if="freeLicences.qt && index === paidLicences.length - 1"
                        effect="dark"
                        class="bold uppercase licence-free-tag pabsolute side-element"
                        type="success"
                        disableTransitions
                        size="small"
                    >
                        {{ labelFreeLicences }}
                    </el-tag>

                    <!-- Delete Custom Pricing -->
                    <el-tooltip
                        v-if="index !== paidLicences.length - 1 && isSaleUser"
                        :content="$t.__('pages.account.LicenceProduct.delete_step')"
                        placement="bottom-start"
                    >
                        <CompassIconButton
                            ref="delete-step"
                            size="medium"
                            icon="bin"
                            class="side-element hover-primary-05-bkg transparent-bkg gray-100 b-0 delete-price pabsolute grey"
                            @click="deleteLevel(pricing)"
                        />
                    </el-tooltip>
                </div>
            </div>
            <!-- Add Custom Pricing -->
            <CompassLink
                v-if="isSaleUser"
                ref="add-step"
                :label="$t.__('common.actions.add_step')"
                type="primary"
                size="medium"
                prefixIcon="plus"
                class="ml-3 mt-2"
                @click="addLevel"
            />

            <!-- Warning allocated licences -->
            <card
                v-if="isSaleUser && licencesBeDeleted"
                ref="warn-delete-licence"
                background="warning-05"
                borderColor="warning-50"
                class="mt-3"
            >
                {{
                    $l10n.__(
                        'Vous êtes sur le point de supprimer une ou plusieurs licences attribuées.',
                    )
                }}
                <br />
                {{
                    $l10n.__(
                        'En continuant, le ou les derniers utilisateurs se verront retirer leurs licences.',
                    )
                }}
            </card>

            <!-- Free Licences block / Only for Sellsy Sales -->
            <div v-if="isSaleUser" class="gray-0-bkg p-3 mt-3 br">
                <div class="flex-row jc-space">
                    <span class="semibold block" :class="{ 'mb-3': showFree }">
                        {{ $l10n.__('Licence offerte') }}
                    </span>
                    <el-switch
                        ref="switch-free"
                        v-model="showFree"
                        inactiveColor="#565665"
                        activeColor="#39CF86"
                        @change="handleFreeLicences"
                    >
                    </el-switch>
                </div>
                <div v-if="showFree" class="flex-row ai-center flex-1">
                    <el-input-number
                        v-model="freeLicences.qt"
                        class="licence-product-quantity"
                        stepStrictly
                        :min="0"
                        @change="updateFree"
                    ></el-input-number>
                    <span class="ml-4">
                        {{ getPriceAmountByMonth(basePriceFreeLicence / 100) }} €<span class="fs-2">
                            {{ $l10n.__('/mois') }}
                        </span>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Card from 'vcomponents/Card.vue';
import QuantityProduct from './QuantityProduct.vue';

export default {
    components: {
        Card,
        QuantityProduct,
    },
    props: {
        modelValue: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        product: {
            type: Object,
            required: true,
        },
        plan: {
            type: String,
            required: true,
        },
        userType: {
            type: String,
            required: true,
        },
        numformat: {
            type: Object,
            required: true,
        },
        saleLimitation: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            freeLicences: {
                up_to: 0,
                unit_amount: 0,
                qt: 0,
            },
            showFree: false,
            showTooltip: false,
        };
    },
    computed: {
        /**
         * Get product plan configuration by current Plan selected
         */
        currentProductPlan() {
            return this.product.plans.find((plan) => plan.code === this.plan);
        },
        /**
         * Get allowed pricing amount and format to use on select
         */
        allowedAmounts() {
            return this.currentProductPlan.allowedCustomPricingAmountsInCents.map((amount) => {
                return {
                    label: this.formatAmount(this.isYearly ? amount / 100 / 12 : amount / 100),
                    amount,
                };
            });
        },
        paidLicences() {
            return this.modelValue.customPricing.filter((price) => price.unit_amount !== 0);
        },
        basePriceFreeLicence() {
            return this.modelValue.customPricing.find((price) => price.up_to === 'inf').unit_amount;
        },
        isSaleUser() {
            return this.userType === 'sale';
        },
        labelFreeLicences() {
            return this.isSaleUser
                ? this.$l10n.n__(
                    '+ %s offerte',
                    '+ %s offertes',
                    this.freeLicences.qt,
                    this.freeLicences.qt,
                )
                : this.$l10n.n__(
                    'Dont %s offerte',
                    'Dont %s offertes',
                    this.freeLicences.qt,
                    this.freeLicences.qt,
                );
        },
        isYearly() {
            return this.currentProductPlan.billingInterval === 'year';
        },
        licencesBeDeleted() {
            return this.modelValue.quantity < this.modelValue.availablesLicences;
        },
        amountLabel() {
            return this.$l10n.__(
                'Pour modifier le tarif d’une licence, %(br)s merci d’ajouter un palier supplémentaire',
                { br: '<br>' },
            );
        },
    },
    watch: {
        modelValue: function (newVal) {
            this.updateLocalFreeLicences(newVal);
        },
    },
    created() {
        this.updateLocalFreeLicences(this.modelValue);
    },
    methods: {
        /**
         * Add or update Free licences step in customPricing array
         */
        updateFree() {
            if (!Number.isInteger(this.freeLicences.qt)) {
                this.freeLicences.qt = Math.round(this.freeLicences.qt);
            }

            let freePricing = this.modelValue.customPricing.find(
                (price) => price.unit_amount === 0,
            );
            if (this.modelValue.customPricing.find((price) => price.unit_amount === 0)) {
                freePricing = {
                    ...freePricing,
                    ...this.freeLicences,
                };
            } else {
                this.modelValue.customPricing.unshift(this.freeLicences);
            }
            this.emit();
        },
        /**
         * Send array pricing for current product on Cart
         * Free licences can't be greater than paids, then on paid decrease, we need to update free quantity
         */
        update(value, index) {
            /**
             * Round quantity when its not integer, work fine with step-stricly props
             */
            if (_.isNil(value)) return;

            if (!Number.isInteger(value)) {
                this.paidLicences[index].qt = Math.round(value);
            } else {
                this.paidLicences[index].qt = this.isSaleUser
                    ? value
                    : value - this.freeLicences.qt;
            }

            this.emit();
        },
        emit() {
            this.$emit('update:modelValue', {
                [this.product.id]: this.modelValue,
            });
        },
        /**
         * On Free Licences block close, reset free quantity
         */
        handleFreeLicences(show) {
            if (!show) {
                this.showFree = show;
                this.freeLicences.qt = 0;
                this.updateFree();
            }
        },
        getPriceAmountByMonth(price) {
            return this.isYearly ? price / 12 : price;
        },
        updateLocalFreeLicences(value) {
            const freeTier = value.customPricing.find((price) => price.unit_amount === 0);
            if (freeTier) {
                if (freeTier.qt !== this.freeLicences.qt) this.showFree = !!freeTier.qt;
                this.freeLicences = freeTier;
            }
        },
        addLevel() {
            const lastLevel = this.paidLicences.find((licence) => licence.unit_amount !== 0);
            const lastPrice = lastLevel.unit_amount;
            const lastPriceIndex =
                this.currentProductPlan.allowedCustomPricingAmountsInCents.findIndex(
                    (price) => price === lastPrice,
                );

            this.modelValue.customPricing.unshift({
                qt: 0,
                unit_amount:
                    this.currentProductPlan.allowedCustomPricingAmountsInCents[lastPriceIndex - 1],
                up_to: lastLevel.qt,
            });
            this.emit();
        },
        formatAmount(amount) {
            return amount % 1 === 0
                ? amount
                : this.$filters.numberFormatWithOptions(amount, this.numformat);
        },
        deleteLevel(amount) {
            this.modelValue.customPricing.splice(
                this.modelValue.customPricing.findIndex(
                    (price) => price.unit_amount === amount.unit_amount,
                ),
                1,
            );
            this.emit();
        },
        disableAmount(amount) {
            return (
                amount === this.currentProductPlan.amountInCents ||
                this.paidLicences.map((licence) => licence.unit_amount).includes(amount)
            );
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;
.licence-product {
    .licence-product-quantity {
        width: 118px;

        &.licence-deleted {
            .el-input__inner {
                border: 1px solid $warning-50;
            }
        }

        &.is-disabled {
            .el-input-number__decrease,
            .el-input-number__decrease:hover,
            .el-input-number__increase,
            .el-input-number__increase:hover,
            .el-input__inner {
                background-color: $gray-05;
                color: $gray-60;
                cursor: pointer;
            }

            .el-input__inner {
                border-color: $gray-40;
            }
        }

        .el-input-number__decrease,
        .el-input-number__increase {
            &.is-disabled {
                cursor: pointer;
            }
        }
    }

    .licence-product-amount {
        width: 96px;

        .el-input.is-disabled {
            .el-select__caret {
                color: $gray-60;
            }

            .el-input__inner {
                background-color: $gray-05;
            }
        }
    }

    .licence-product-title {
        flex-basis: 152px;
    }

    .licence-product-title,
    .el-tag {
        margin-top: 6px;
    }

    .licence-product-inner {
        max-width: 300px;
        overflow-y: visible;
    }

    .licence-product-row {
        &:hover {
            .delete-price {
                opacity: 1;
            }
        }
    }

    .el-input-number__decrease,
    .el-input-number__increase {
        background: $white;

        &.is-disabled {
            .el-icon-minus {
                pointer-events: none;
            }
        }
    }

    .licence-free-tag {
        font-size: 10px;
    }

    .side-element {
        left: 100%;
    }

    .delete-price {
        opacity: 0;
    }
}

.licence-product-tooltip.el-tooltip__popper {
    padding: 4px 8px;

    span {
        line-height: 16px;
    }
}
</style>
